<template>
  <div>
    <!-- EDITOR -->
    <div class="row q-col-gutter-md q-mb-sm">
      <model-form-input
        :model="section"
        field="config.showtimesWidgetTitle"
        value-type="String"
        class="col-10"
        label="Title"
        :rules="[val => val !== null || 'Field is required']"
      />
      <model-form-toggle
        :model="section"
        field="config.showShowtimesWidgetTitle"
        value-type="Boolean"
        class="col-2 q-pb-md"
        label="Show Title"
      />
    </div>

    <div
      class="row q-mb-sm"
    >
      <div class="text-bold">
        Date Filter
      </div>
    </div>
    <div
      class="row q-col-gutter-md q-mb-md"
      :style="$q.screen.lt.md ? '' : 'height: 120px'"
    >
      <q-toggle
        v-model="section.config.showShowtimesWidgetDateFilter"
        class="col-3 q-pb-md"
        label="Show Date Filter"
      />
      <q-select
        v-model="section.config.showtimesDatePickerType"
        class="col"
        label="Date Picker Style"
        :options="$mh.asOptions(SHOWTIMES_DATE_PICKER_TYPES)"
        option-label="name"
        outlined
        stack-label
        :disable="!section.config.showShowtimesWidgetDateFilter"
        map-options
        emit-value
      >
        <q-tooltip v-if="isMultiSiteMoviePage">
          Date picker style is automatically set to SWIPER for multi-site movie pages
        </q-tooltip>
        <template #selected-item="scope">
          <showtimes-date-filter
            class="q-pa-none q-mx-none"
            style="transform: scale(0.6) translate(-30%, 0px);"
            :showtimes-date-picker-type="scope.opt.value"
            :showtimes-allow-all="section.config.showtimesAllowAll"
            :is-showtimes-dark-mode="$q.dark.isActive && $consumer"
            :border-radius="section.config.showtimesButtonBorderRadius"
            center-align-date-filter
            display-dates-without-showings
          />
        </template>
        <template #option="scope">
          <q-item
            v-bind="scope.itemProps"
            v-on="scope.itemEvents"
          >
            <q-item-section class="row justify-start items-start">
              <showtimes-date-filter
                class="q-pa-none col-auto"
                style="transform: scale(0.6) translate(-30%, 0px);"
                :showtimes-date-picker-type="scope.opt.value"
                :showtimes-allow-all="section.config.showtimesAllowAll"
                :is-showtimes-dark-mode="$q.dark.isActive && $consumer"
                :border-radius="section.config.showtimesButtonBorderRadius"
                center-align-date-filter
                display-dates-without-showings
              />
            </q-item-section>
          </q-item>
        </template>
      </q-select>
      <q-toggle
        v-model="section.config.showtimesAllowAll"
        class="col-3"
        label="Showtimes Allow All"
        bottom-slots
        :disable="!section.config.showShowtimesWidgetDateFilter || section.config.showtimesDatePickerType !== SHOWTIMES_DATE_PICKER_TYPES.TOGGLE"
      >
        <q-tooltip v-if="section.config.showtimesDatePickerType !== SHOWTIMES_DATE_PICKER_TYPES.TOGGLE || !section.config.showShowtimesWidgetDateFilter">
          Only available when Date Picker Style is set to first option and Date Filter is enabled
        </q-tooltip>
        <q-tooltip v-else>
          Enabled showtimes to show all dates at once
        </q-tooltip>
      </q-toggle>
    </div>
    <div
      class="row q-mb-sm"
    >
      <div class="text-bold">
        Site Selector
      </div>
    </div>
    <div
      class="row q-col-gutter-md q-mb-sm"
    >
      <q-toggle
        v-model="showSiteSelector"
        class="col-3"
        label="Show Site Selector"
        bottom-slots
      >
        <q-tooltip v-if="isMultiSiteMoviePage">
          Site selector is automatically enabled for multi-site movie pages
        </q-tooltip>
      </q-toggle>

      <q-select
        v-if="showSiteSelector"
        v-model="section.config.showtimesWidgetSiteSelectorDisplayTypes"
        class="col-6"
        label="Site Selector Display Type"
        :options="$mh.asOptions(SITE_INFO_DISPLAY_TYPES)"
        multiple
        clearable
        map-options
        emit-value
        use-chips
      >
        <q-tooltip v-if="isMultiSiteMoviePage">
          Display type is automatically set to TABS for multi-site movie pages
        </q-tooltip>
      </q-select>
      <q-toggle
        v-if="showSiteSelector && hasSiteLocationZones"
        v-model="section.config.showtimesWidgetFlattenZoneGroups"
        class="col-3"
        label="Display All Sites(No Zones Grouping)"
        bottom-slots
        :disable="!section.config.showtimesWidgetSiteSelectorDisplayTypes || !hasSiteLocationZones"
      >
        <q-tooltip>
          Site will be grouped with zones that are associated with the site
        </q-tooltip>
      </q-toggle>
    </div>
    <div
      class="row q-mb-sm"
    >
      <div class="text-bold">
        Movies Display
      </div>
    </div>
    <div class="row q-col-gutter-md q-mb-sm">
      <iso-date-input
        v-model="section.config.date"
        emit-string="nullify-blank"
        class="col-6"
        label="Date"
        clearable
        @update:model-value="changeCount++"
      />
      <q-select
        v-model="section.titleClassIds"
        label="Title Classes"
        map-options
        :options="titleClasses.data"
        clearable
        option-label="name"
        option-value="id"
        emit-value
        :style="{ width: $q.screen.lt.sm ? '150px' : undefined }"
        class="col-6"
        multiple
        use-chips
      />
    </div>
    <div class="row q-col-gutter-sm q-mb-sm q-mt-sm">
      <q-select
        v-if="section.movies"
        v-model="section.movies.data"
        class="col-6"
        label="Movies"
        :options="movies.data"
        option-label="name"
        outlined
        multiple
        clearable
        :use-input="true"
        input-style="search"
        input-debounce="100"
        stack-label
        @filter="filterMovies"
      >
        <template #selected-item="scope">
          <q-chip
            v-if="scope.opt.name"
            removable
            dense
            :style="'color: ' + scope.opt.color + '; border-color: ' + scope.opt.color + '; background-color: ' + scope.opt.color + '33;'"
            :tabindex="scope.tabindex"
            :label="scope.opt.name"
            @remove="scope.removeAtIndex(scope.index)"
          />
        </template>
        <template #option="scope">
          <q-item
            v-bind="scope.itemProps"
            v-on="scope.itemEvents"
          >
            <q-item-section>
              {{ scope.opt.name }}
            </q-item-section>
          </q-item>
        </template>
      </q-select>
      <q-select
        v-if="section.showings"
        v-model="section.showings.data"
        label="Showings"
        class="col-6"
        :options="showings.data"
        option-label="description"
        multiple
        outlined
        clearable
        :use-input="true"
        input-style="search"
        input-debounce="300"
        stack-label
        @filter="filterShowings"
      >
        <template #selected-item="scope">
          <q-chip
            v-if="scope.opt.description"
            removable
            dense
            style="max-width: 280px;"
            :style="'color: ' + scope.opt.movie.color + '; border-color: ' + scope.opt.movie.color + '; background-color: ' + scope.opt.movie.color + '33;'"
            :tabindex="scope.tabindex"
            :label="scope.opt.description"
            @remove="scope.removeAtIndex(scope.index)"
          >
            <q-tooltip>{{ scope.opt.description }}</q-tooltip>
          </q-chip>
        </template>
        <template #option="scope">
          <q-item
            v-bind="scope.itemProps"
            v-on="scope.itemEvents"
          >
            <q-item-section>
              {{ scope.opt.description }}
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm q-mt-sm">
      <showing-badge-select
        :model="section"
        label="Prefilter Showing Badges"
        field-name="filterShowingBadgeIds"
        class="col-6"
      />
      <q-checkbox
        v-model="section.config.matchAllShowingBadges"
        class="col-6"
        label="Match All Showing Badges"
        bottom-slots
      >
        <q-tooltip>
          Show multiple matching showing badges
        </q-tooltip>
      </q-checkbox>
    </div>
    <div
      class="row q-mb-sm"
    >
      <div class="text-bold">
        Filter Display
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-sm">
      <showing-badge-select
        v-if="!section.config.matchAllShowingBadges"
        :model="section"
        label="Showing Badges Filter Options"
        field-name="filterOptionShowingBadgeIds"
        class="col-6 col-sm-6"
      />
      <q-toggle
        v-model="section.config.useClassFilter"
        class="col"
        label="Show Class Filter"
        bottom-slots
      >
        <q-tooltip>
          If showing results are limited to specific title classes, those title classes will be used for the filter. If not, the filter will be based on the title classes from all showing results.
        </q-tooltip>
      </q-toggle>
    </div>

    <div class="row q-mb-sm q-mt-lg">
      <div class="text-bold">
        Showtimes Display
      </div>
    </div>
    <div class="row q-col-gutter-md q-mb-md">
      <q-select
        v-model="section.config.showtimesDisplayType"
        class="col-6 col-sm-3"
        label="Default Display Type"
        :options="showtimesDisplayTypeOptions"
        map-options
        emit-value
      />
      <q-checkbox
        v-model="section.config.showtimesDisplayTypeAllowToggle"
        class="col-6 col-sm-3"
        label="Allow display toggle"
        bottom-slots
      >
        <q-tooltip>
          When enabled, the user will be able to toggle between list and grid display types.
        </q-tooltip>
      </q-checkbox>

      <div
        class="col-6 col-sm-3"
      >
        <q-item-label>Border Radius</q-item-label>
        <q-slider
          v-model="section.config.borderRadius"
          label
          label-always
          switch-label-side
          type="number"
          :min="0"
          :max="32"
          :step="4"
        />
        <q-tooltip>
          Border radius will apply to post and buttons.
        </q-tooltip>
      </div>
      <div
        class="col-6 col-sm-3"
      >
        <q-item-label>Shadow</q-item-label>
        <q-slider
          v-model="section.config.shadow"
          label
          label-always
          switch-label-side
          type="number"
          :min="0"
          :max="10"
          :step="1"
        />
      </div>

      <q-toggle
        v-model="section.config.isShowtimesDarkMode"
        class="col-6 col-sm-3"
        label="Dark Mode"
        :disable="$q.dark.isActive"
        bottom-slots
      >
        <q-tooltip v-if="$q.dark.isActive">
          Widget Dark Mode is disabled because the site-wide Dark Mode is enabled.
        </q-tooltip>
      </q-toggle>

      <q-checkbox
        v-model="section.config.useBanner"
        class="col-6 col-sm-3"
        label="Use Banner Image"
        bottom-slots
      />
      <q-checkbox
        v-model="section.config.cleanImage"
        class="col-6 col-sm-3"
        label="Clean Image"
        bottom-slots
      />
      <q-checkbox
        v-model="section.config.showClass"
        class="col-6 col-sm-3"
        label="Show Movie Class"
        bottom-slots
      />
      <q-checkbox
        v-model="section.config.showTrailerButton"
        class="col-6 col-sm-3"
        label="Show Trailer Button"
        bottom-slots
      >
        <q-tooltip>
          Show trailer button if there is a trailer for the movie
        </q-tooltip>
      </q-checkbox>
      <q-checkbox
        v-model="section.config.enableLeavingSoonBanner"
        class="col-6 col-sm-3"
        label="Enable Leaving Soon Banner"
        bottom-slots
      />
      <q-checkbox
        v-model="section.config.showDateHeading"
        class="col-6 col-sm-3"
        label="Show Date Heading"
        bottom-slots
      />
      <q-checkbox
        v-model="section.config.showShowingBadge"
        class="col-6 col-sm-3"
        label="Show Showing Badge"
        bottom-slots
      />
      <color-selection
        class="col-6"
        :color-name="section.config.showtimesBlocksBackgroundColor"
        :custom-color-hex="section.config.showtimesBlocksBackgroundColorHex"
        label="Background Color"
        @update:model-value="setBackgroundColor"
      />
      <q-checkbox
        v-if="section.config.cleanImage"
        v-model="section.config.showtimesBlocksTextLight"
        class="col-6 col-sm-3"
        label="Text Light"
        bottom-slots
      />
    </div>
    <div class="row q-mb-sm q-mt-lg">
      <div class="text-bold">
        Showtimes View
      </div>
    </div>

    <div class="row q-col-gutter-md q-mb-md">
      <div class="col-6">
        <q-radio
          v-model="section.config.showtimesViewType"
          :val="SHOWTIMES_VIEW_TYPES.BLOCK"
        >
          <div class="text-center row self-center items-center">
            <img
              src="/icons/block.png"
              class="q-mr-sm"
              style="width: 50px;"
              :style="{ filter: $q.dark.isActive ? 'brightness(0) invert(1)' : undefined }"
            > Block
          </div>
        </q-radio>
      </div>
      <div class="col-6">
        <q-radio
          v-model="section.config.showtimesViewType"
          :val="SHOWTIMES_VIEW_TYPES.SLIDER"
        >
          <div class="text-center row self-center items-center">
            <img
              src="/icons/slider.png"
              class="q-mr-sm"
              style="width: 50px;"
              :style="{ filter: $q.dark.isActive ? 'brightness(0) invert(1)' : undefined }"
            > Slider
          </div>
        </q-radio>
      </div>
    </div>
    <div
      v-if="section.config.showtimesViewType === SHOWTIMES_VIEW_TYPES.SLIDER"
      class="row q-mb-sm q-mt-lg"
    >
      <q-checkbox
        v-model="section.config.showtimesSliderControlsVisible"
        class="col-3"
        label="Slider Controls Visible"
        bottom-slots
      />
      <q-checkbox
        v-model="section.config.showtimesSliderPaginationVisible"
        class="col-3"
        label="Slider Pagination Visible"
        bottom-slots
      />
      <q-checkbox
        v-model="section.config.showtimesSliderPaginateGroupedSlides"
        class="col-3"
        label="Paginate Slides in Groups"
        bottom-slots
      />
      <q-checkbox
        v-model="section.config.showtimesSliderAutoplay"
        class="col-3"
        label="Slider Autoplay"
        bottom-slots
      />
      <q-input
        v-if="section.config.showtimesSliderAutoplay"
        v-model.number="section.config.showtimesSliderDelayInSeconds"
        class="col-3"
        label="Slider Autoplay Delay (seconds)"
        type="number"
        min="0"
        max="120"
        step="1"
      />
    </div>
    <template v-if="section.config.showtimesDisplayType === SHOWTIMES_DISPLAY_TYPES.LIST || section.config.showtimesDisplayTypeAllowToggle">
      <div
        class="row q-mb-sm q-mt-lg"
      >
        <div class="text-bold">
          Showtimes List Display
        </div>
      </div>
      <div class="row q-col-gutter-md q-mb-md">
        <q-select
          v-if="section.config.showtimesDisplayType === SHOWTIMES_DISPLAY_TYPES.LIST || section.config.showtimesDisplayTypeAllowToggle"
          v-model="section.config.showtimesListButtonType"
          class="col-6 col-sm-3"
          label="List Display Button Type"
          :options="showtimesButtonTypeOptions"
          map-options
          emit-value
        />

        <q-checkbox
          v-model="section.config.showtimesListButtonUseProgressBar"
          class="col-6 col-sm-3"
          label="Show Button Progress Bar"
          :disable="disableProgressBarDisplay"
          bottom-slots
        >
          <q-tooltip v-if="disableProgressBarDisplay">
            This option is disabled when showtimes widget is configured to display movies for multiple sites.
          </q-tooltip>
        </q-checkbox>

        <q-checkbox
          v-model="section.config.bannerBackground"
          class="col-6 col-sm-3"
          label="Banner Background"
          bottom-slots
        />
        <div
          v-if="section.config.bannerBackground"
          class="col-6 col-sm-3"
        >
          <q-item-label>Banner Brightness</q-item-label>
          <q-slider
            v-model="section.config.bannerBrightness"
            label
            label-always
            switch-label-side
            type="number"
            :min="0"
            :max="1"
            :step="0.05"
            @update:model-value="changeCount++"
          />
        </div>
        <div
          v-if="section.config.bannerBackground"
          class="col-6 col-sm-3"
        >
          <q-item-label>Banner Opacity</q-item-label>
          <q-slider
            v-model="section.config.bannerOpacity"
            label
            label-always
            switch-label-side
            type="number"
            :min="0"
            :max="1"
            :step="0.05"
            @update:model-value="changeCount++"
          />
        </div>
        <q-checkbox
          v-model="section.config.showtimesListShowMovieImage"
          class="col-6 col-sm-3"
          label="Show Movie Image"
          bottom-slots
        />
        <q-checkbox
          v-model="section.config.showtimesListShowMovieSynopsis"
          class="col-6 col-sm-3"
          label="Show Movie Synopsis"
          bottom-slots
        />
        <q-checkbox
          v-if="section.config.showtimesListShowMovieSynopsis"
          v-model="section.config.showtimesListMovieSynopsisWrapped"
          class="col-6 col-sm-3"
          label="Truncate movie synopsis"
          bottom-slots
        >
          <q-tooltip>Having it checked would make Synopsis shorter, but having it unchecked would keep the full Synopsis</q-tooltip>
        </q-checkbox>
        <div
          v-if="section.config.showtimesListShowMovieSynopsis && section.config.showtimesListMovieSynopsisWrapped"
          class="col-6 col-sm-3"
        >
          <q-item-label>Maximum Mobile Truncated Synopsis Line Count</q-item-label>
          <q-slider
            v-model="section.config.mobileTruncatedLineCount"
            label
            label-always
            switch-label-side
            type="number"
            :min="3"
            :max="21"
            :step="3"
          />
        </div>
        <div
          v-if="section.config.showtimesListShowMovieSynopsis && section.config.showtimesListMovieSynopsisWrapped"
          class="col-6 col-sm-3"
        >
          <q-item-label>Maximum Truncated Movie Synopsis Lines Count</q-item-label>
          <q-slider
            v-model="section.config.truncatedLineCount"
            label
            label-always
            switch-label-side
            type="number"
            :min="3"
            :max="21"
            :step="3"
          />
        </div>
      </div>
    </template>
    <template v-if="section.config.showtimesDisplayType === SHOWTIMES_DISPLAY_TYPES.GRID || section.config.showtimesDisplayTypeAllowToggle">
      <div
        class="row q-mb-sm q-mt-lg"
      >
        <div class="text-bold">
          Showtimes Grid Display
        </div>
      </div>
      <div class="row q-col-gutter-md q-mb-md">
        <q-select
          v-model="section.config.showtimesGridButtonType"
          class="col-6 col-sm-3"
          label="Grid Display Button Type"
          :options="showtimesButtonTypeOptions"
          map-options
          emit-value
        />
        <q-checkbox
          v-if="section.config.showtimesGridButtonType === SHOWTIMES_BUTTON_TYPES.COMPACT"
          v-model="section.config.showtimesGridButtonUseButtonDrawer"
          class="col-6 col-sm-3"
          label="Use Button Drawer"
          bottom-slots
        />
        <q-checkbox
          v-model="section.config.showtimesGridButtonUseProgressBar"
          class="col-6 col-sm-3"
          label="Show Button Progress Bar"
          :disable="disableProgressBarDisplay"
          bottom-slots
        >
          <q-tooltip v-if="disableProgressBarDisplay">
            This option is disabled when showtimes widget is configured to showing and movies for multiple sites.
          </q-tooltip>
        </q-checkbox>
        <div
          class="col-6 col-sm-3"
        >
          <q-item-label>Padding</q-item-label>
          <q-slider
            v-model="section.config.showtimesSliderGutter"
            label
            label-always
            switch-label-side
            type="number"
            :min="0"
            :max="32"
            :step="4"
            @update:model-value="changeCount++"
          />
        </div>
        <div
          v-if="section.config.showtimesViewType === SHOWTIMES_VIEW_TYPES.BLOCK"
          class="col-6 col-sm-3"
        >
          <q-item-label>Max Movies Per Row</q-item-label>
          <q-slider
            v-model="section.config.showtimesGridPerRow"
            label
            label-always
            switch-label-side
            type="number"
            :min="2"
            :max="6"
            :step="1"
            @update:model-value="changeCount++"
          />
          <q-tooltip>Desktop only</q-tooltip>
        </div>
        <div
          v-if="section.config.showtimesViewType === SHOWTIMES_VIEW_TYPES.SLIDER"
          class="col-6 col-sm-3"
        >
          <q-item-label>Max Showtimes Per View</q-item-label>
          <q-slider
            v-model="section.config.showtimesSliderPerView"
            label
            label-always
            switch-label-side
            type="number"
            :min="2"
            :max="5"
            :step="1"
            @update:model-value="changeCount++"
          />
        </div>
      </div>
    </template>
    <div
      class="row q-mb-sm q-mt-lg"
    >
      <div class="text-bold">
        Button
      </div>
    </div>
    <div class="row q-col-gutter-md q-mb-md">
      <q-checkbox
        v-model="section.config.showAttendanceLevels"
        class="col-6 col-sm-3"
        label="Show Attendance Levels"
        bottom-slots
      />
      <q-checkbox
        v-if="section.config.showtimesListButtonType === SHOWTIMES_BUTTON_TYPES.BASIC || section.config.showtimesGridButtonType === SHOWTIMES_BUTTON_TYPES.BASIC"
        v-model="section.config.solidShowingBadges"
        class="col-6 col-sm-3"
        label="Solid Attendance Badges"
        bottom-slots
      />
      <button-style-editor
        v-if="section.config.showtimesListButtonType !== SHOWTIMES_BUTTON_TYPES.BASIC && section.config.showtimesGridButtonType !== SHOWTIMES_BUTTON_TYPES.BASIC"
        class="row col-12"
        :button-type="showButtonTypeEditor ? section.config.showtimesButtonType : null"
        :color-name="section.config.showtimesButtonColor"
        :custom-color-hex="section.config.showtimesButtonColorHex"
        :button-border-radius="section.config.showtimesButtonBorderRadius"
        @update:model-value="setButtonStyle"
      />
    </div>

    <preview
      v-if="!hidePreview"
      :key="changeCount"
      :section="section"
      class="q-mb-xl"
    />
  </div>
</template>

<script>

import { date } from 'quasar'
import { IsoDate } from 'src/models/model'
import Section, { BUTTON_TYPES, SHOWTIMES_BUTTON_TYPES, SHOWTIMES_DATE_PICKER_TYPES, SHOWTIMES_DISPLAY_TYPES, SHOWTIMES_VIEW_TYPES, SITE_INFO_DISPLAY_TYPES } from 'src/models/page/Section'
import ButtonStyleEditor from 'src/modules/admin/components/ButtonStyleEditor'
import ColorSelection from 'src/modules/admin/components/ColorSelection'
import IsoDateInput from 'src/modules/admin/components/IsoDateInput'
import ShowingBadgeSelect from 'src/modules/admin/showing-badge/components/ShowingBadgeSelect'
import Preview from 'src/modules/consumer/components/Preview'
import ShowtimesDateFilter from 'src/modules/consumer/components/ShowtimesDateFilter'
const { formatDate } = date

export default {
  name: 'ShowtimesEditor',
  configuration: ['titleClasses', 'currentSite', 'sites'],
  components: {
    IsoDateInput,
    ShowingBadgeSelect,
    Preview,
    ColorSelection,
    ButtonStyleEditor,
    ShowtimesDateFilter
  },
  props: {
    section: {
      type: Section,
      required: true
    },
    hidePreview: {
      type: Boolean,
      default: false
    }
  },
  models: {
    movies: {
      variables () {
        return {
          searchString: this.movieSearchString,
          orderBy: 'createdAt',
          descending: true,
          limit: 10
        }
      }
    },
    showings: {
      movie: {
        titleClass: true
      },
      variables () {
        return {
          searchString: this.showingSearchString,
          orderBy: 'time',
          period: 'future',
          limit: 10
        }
      }
    },
    siteLocationZones: {
      sites: 'default'
    }
  },
  data () {
    return {
      changeCount: 0,
      dateSelection: 'all',
      showingSearchString: '',
      movieSearchString: '',
      showSiteSelector: false
    }
  },
  computed: {
    showButtonTypeEditor () {
      if (this.section.config.showtimesDisplayTypeAllowToggle) return this.section.config.showtimesListButtonType === SHOWTIMES_BUTTON_TYPES.COMPACT || this.section.config.showtimesGridButtonType === SHOWTIMES_BUTTON_TYPES.COMPACT
      if (this.section.config.showtimesDisplayType === SHOWTIMES_DISPLAY_TYPES.LIST) return this.section.config.showtimesListButtonType === SHOWTIMES_BUTTON_TYPES.COMPACT
      return this.section.config.showtimesGridButtonType === SHOWTIMES_BUTTON_TYPES.COMPACT
    },
    isMultiSiteMoviePage () {
      return this.currentSite.consumerConfig.showMoviesAndShowingsForMultipleSites
    },
    hasSiteLocationZones () {
      const siteLocationZones = _.filter(this.siteLocationZones.data, locationZone => !_.isEmpty(locationZone.visibleSites))
      return !_.isEmpty(siteLocationZones)
    },
    disableProgressBarDisplay () {
      return !!this.currentSite.consumerConfig.showMoviesAndShowingsForMultipleSites && this.showSiteSelector
    }
  },
  watch: {
    showSiteSelector () {
      if (!this.showSiteSelector) {
        this.section.config.showtimesWidgetSiteSelectorDisplayTypes = []
      }
      if (this.disableProgressBarDisplay) {
        this.section.config.showtimesListButtonUseProgressBar = false
        this.section.config.showtimesGridButtonUseProgressBar = false
      }
    },
    '$q.dark.isActive': {
      handler () {
        if (this.$q.dark.isActive) {
          this.section.config.isShowtimesDarkMode = true
        }
      },
      immediate: true
    },
    isMultiSiteMoviePage: {
      handler () {
        if (this.isMultiSiteMoviePage) {
          // set default UI view/style for multi-site showtimes
          this.showSiteSelector = true
          this.section.config.showtimesWidgetSiteSelectorDisplayTypes = [SITE_INFO_DISPLAY_TYPES.TABS]
          this.section.config.showtimesDatePickerType = SHOWTIMES_DATE_PICKER_TYPES.SWIPER
        } else {
          this.showSiteSelector = false
          this.section.config.showtimesWidgetSiteSelectorDisplayTypes = []
        }
        if (this.disableProgressBarDisplay) {
          this.section.config.showtimesListButtonUseProgressBar = false
          this.section.config.showtimesGridButtonUseProgressBar = false
        }
      },
      immediate: true
    },
    'section.config.showtimesWidgetSiteSelectorDisplayTypes': {
      handler () {
        if (_.isNil(this.section.config.showtimesWidgetSiteSelectorDisplayTypes) || _.isEmpty(this.section.config.showtimesWidgetSiteSelectorDisplayTypes)) {
          this.showSiteSelector = false
        } else {
          this.showSiteSelector = true
        }
      }
    }
  },
  created () {
    _.extend(this, {
      showtimesViewTypeOptions: this.$mh.asOptions(SHOWTIMES_VIEW_TYPES),
      showtimesDisplayTypeOptions: this.$mh.asOptions(SHOWTIMES_DISPLAY_TYPES),
      showtimesButtonTypeOptions: this.$mh.asOptions(SHOWTIMES_BUTTON_TYPES),
      SHOWTIMES_DISPLAY_TYPES,
      SHOWTIMES_BUTTON_TYPES,
      SHOWTIMES_VIEW_TYPES,
      SHOWTIMES_DATE_PICKER_TYPES,
      SITE_INFO_DISPLAY_TYPES,
      BUTTON_TYPES,
      IsoDate,
      formatDate
    })
  },
  mounted () {
    if (this.currentSite.consumerConfig.showMoviesAndShowingsForMultipleSites) {
      this.section.config.showtimesWidgetSiteSelectorDisplayTypes = SITE_INFO_DISPLAY_TYPES.TABS
      this.showSiteSelector = true
    } else {
      this.showSiteSelector = false
      this.section.config.showtimesWidgetSiteSelectorDisplayTypes = []
    }
  },
  methods: {
    filterShowings (val, update, abort) {
      update(() => {
        this.showingSearchString = val
      })
    },
    filterMovies (val, update, abort) {
      update(() => {
        this.movieSearchString = val
      })
    },
    setBackgroundColor (colorName, customColorHex) {
      this.section.config.showtimesBlocksBackgroundColor = colorName
      this.section.config.showtimesBlocksBackgroundColorHex = customColorHex
    },
    setButtonStyle (buttonType, colorName, customColorHex, buttonRadius) {
      this.section.config.showtimesButtonType = buttonType
      this.section.config.showtimesButtonColor = colorName
      this.section.config.showtimesButtonColorHex = customColorHex
      this.section.config.showtimesButtonBorderRadius = buttonRadius
    }
  }
}

</script>

<style lang="scss" scoped>
.border-grey-3{
  border: 3px solid $grey-4;
}
</style>
